import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "./components/AdminLayout";
import { message } from "antd";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./AdminProduct.css";

const AdminProduct = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Optional: if you set REACT_APP_API_BASE="https://yourdomain.com"
  const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");

  const authHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: "Bearer " + token } : {};
  };

  // ✅ Convert ANY stored path into a browser-loadable URL
  // supported:
  // - "productImages/xxx.png"
  // - "/productImages/xxx.png"
  // - "/usr/share/jbs-assets/productImages/xxx.png"
  // - "C:\\...\\productImages\\xxx.png"
  // - full http url
  const getProductImgUrl = (raw) => {
    const s = String(raw || "").trim();
    if (!s) return "";

    // if already full url
    if (s.startsWith("http://") || s.startsWith("https://")) return s;

    // normalize slashes
    const clean = s.replace(/\\/g, "/");

    // take only filename if stored as absolute path
    // try to find "/productImages/"
    const idx = clean.toLowerCase().lastIndexOf("/productimages/");
    let file = "";
    if (idx !== -1) {
      file = clean.substring(idx + "/productImages/".length);
    } else {
      // maybe stored like "productImages/xxx.png" or just "xxx.png"
      file = clean.replace(/^\/+/, "").replace(/^productImages\//i, "");
    }

    if (!file) return "";

    const rel = `/productImages/${file}`;

    // if API_BASE provided use it (useful when admin panel is separate domain)
    return API_BASE ? `${API_BASE}${rel}` : rel;
  };

  const onImgError = (e) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src = "https://via.placeholder.com/80x80?text=No+Image";
  };

  const handleDeleteProduct = async (id) => {
    const shouldDelete = window.confirm("Are you sure to delete?");
    if (!shouldDelete) return;

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        message.error("Token missing. Please login again.");
        navigate("/login");
        return;
      }

      const res = await axios.post(
        "/api/product/delete-product",
        { id },
        { headers: authHeaders() }
      );

      if (res.data?.success) {
        message.success(res.data?.message || "Deleted");

        // ✅ instant UI remove (no wait)
        setProducts((prev) => prev.filter((p) => p._id !== id));
      } else {
        message.error(res.data?.message || "Delete failed");
      }
    } catch (error) {
      console.error(error);
      message.error(error?.response?.data?.message || "Delete failed");
    }
  };

  const getAllProducts = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        message.error("Token missing. Please login again.");
        navigate("/login");
        return;
      }

      const res = await axios.get("/api/product/get-all-products", {
        headers: authHeaders(),
      });

      if (res.data?.success) {
        // keep latest first
        setProducts([...(res.data?.data || [])].reverse());
      } else {
        message.error(res.data?.message || "Failed to fetch products");
      }
    } catch (error) {
      console.log(error);
      if (error?.response?.status === 401) {
        message.error("Unauthorized. Please login again.");
        navigate("/login");
      } else {
        message.error(error?.response?.data?.message || "Failed to fetch products");
      }
    }
  };

  useEffect(() => {
    getAllProducts();
    // eslint-disable-next-line
  }, []);

  const filteredProducts = useMemo(() => {
    const q = String(searchQuery || "").trim().toLowerCase();
    if (!q) return products;
    return (products || []).filter((p) =>
      String(p?.name || "").toLowerCase().includes(q)
    );
  }, [products, searchQuery]);

  return (
    <AdminLayout>
      <div className="admin-users-container">
        <div className="page-title admin-product-title">
          <h3 className="m-0">Products</h3>
          <button className="admin-product-addbtn" onClick={() => navigate("/admin-add-product")}>
            Add New
          </button>
        </div>

        <hr />

        <div className="table-container admin-product-wrap">
          <div className="admin-product-tools">
            <div className="admin-product-search">
              <SearchIcon className="icon" />
              <input
                type="search"
                placeholder="Search by name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* ✅ Desktop Table */}
          <div className="admin-product-table">
            <table className="table user-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Name</th>
                  <th>API Game Name</th>
                  <th>Category</th>
                  <th>Stock</th>
                  <th style={{ width: 120 }}>Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredProducts?.map((product, index) => (
                  <tr key={product?._id || index}>
                    <td>
                      <img
                        className="admin-product-img"
                        src={getProductImgUrl(product?.image)}
                        alt={product?.name || "product"}
                        onError={onImgError}
                      />
                    </td>
                    <td>
                      <div className="admin-product-name">
                        {product?.name || "-"}
                      </div>
                    </td>
                    <td>
                      <span className="badge-soft">
                        {product?.gameName || "-"}
                      </span>
                    </td>
                    <td>
                      <span className="badge-soft">
                        {product?.category || "-"}
                      </span>
                    </td>
                    <td>
                      <span className={`badge-soft ${product?.stock === "yes" ? "ok" : "bad"}`}>
                        {product?.stock || "-"}
                      </span>
                    </td>
                    <td>
                      <div className="admin-product-actions">
                        <button
                          className="iconbtn"
                          onClick={() => navigate(`/admin-edit-product/${product?._id}`)}
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </button>

                        <button
                          className="iconbtn danger"
                          onClick={() => handleDeleteProduct(product?._id)}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {!filteredProducts?.length && (
                  <tr>
                    <td colSpan="6" className="text-center">
                      No products found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ✅ Mobile Cards */}
          <div className="admin-product-cards">
            {filteredProducts?.map((p) => (
              <div className="admin-product-card" key={p._id}>
                <div className="row1">
                  <img
                    className="card-img"
                    src={getProductImgUrl(p?.image)}
                    alt={p?.name || "product"}
                    onError={onImgError}
                  />
                  <div className="info">
                    <div className="title">{p?.name || "-"}</div>
                    <div className="meta">
                      <span className="badge-soft">{p?.gameName || "-"}</span>
                      {p?.category && (
                        <span className="badge-soft">{p.category}</span>
                      )}
                      <span className={`badge-soft ${p?.stock === "yes" ? "ok" : "bad"}`}>
                        {p?.stock || "-"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="row2">
                  <button
                    className="btn-mini"
                    onClick={() => navigate(`/admin-edit-product/${p?._id}`)}
                  >
                    Edit
                  </button>

                  <button
                    className="btn-mini danger"
                    onClick={() => handleDeleteProduct(p?._id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}

            {!filteredProducts?.length && (
              <div className="admin-product-empty">No products found</div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminProduct;
